import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import type { Color } from '#src/atoms/buttons/Button/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { CreateFiatWalletWrapper } from '#src/components/fiatWallet/CreateFiatWalletWrapper/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import {
  WalletTab,
  useWalletDrawerContext,
} from '#src/contexts/walletDrawer/index.tsx';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';

export const Label = ({ minimumAmount }: { minimumAmount: number }) => {
  const { formatNumber } = useIntl();
  const { fiatCurrency } = useFiatBalance();

  return (
    <FormattedMessage
      defaultMessage="Deposit {amount}* or more"
      values={{
        amount: formatNumber(minimumAmount, {
          style: 'currency',
          currency: fiatCurrency,
          minimumFractionDigits: 0,
        }),
      }}
    />
  );
};

const Root = styled(Vertical).attrs({ gap: 1, center: true })`
  width: 100%;
`;

export const Cta = ({
  color = 'primary',
  size = 'medium',
}: {
  color?: Color;
  size?: 'medium' | 'large';
}) => {
  const {
    currentUser,
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const { setCurrentTab, showDrawer } = useWalletDrawerContext();
  const { canDepositAndWithdraw } = useFiatBalance();
  const { cryptocurrencySymbol } = useCurrentUserCurrencies();
  const getDepositTab = () => {
    if (onlyShowFiatCurrency) {
      return WalletTab.ADD_FUNDS_TO_FIAT_WALLET;
    }
    return WalletTab.ADD_FUNDS;
  };

  return (
    <CreateFiatWalletWrapper>
      {({ setShowCreateFiatWallet }) => (
        <Root>
          <Button
            fullWidth
            size={size}
            color={color}
            onClick={() => {
              if (onlyShowFiatCurrency && !canDepositAndWithdraw) {
                setShowCreateFiatWallet(true);
                return;
              }
              showDrawer();
              setCurrentTab(getDepositTab());
            }}
          >
            <Label
              minimumAmount={
                (currentUser?.completablePromotionalEvent?.eligibilityRules
                  .minimumDepositAmount || 0) / 100
              }
            />
          </Button>
          <LabelS color="var(--c-nd-600-opaque)">
            <FormattedMessage
              defaultMessage="*{cryptocurrencySymbol} allowed"
              values={{
                cryptocurrencySymbol,
              }}
            />
          </LabelS>
        </Root>
      )}
    </CreateFiatWalletWrapper>
  );
};
